import React, { useRef } from "react";
import { LocateFixed, Minus, Plus } from "lucide-react";

import { CAMERA } from "./lightsConstants";
import { LIGHTS_TEXT, edgeTooltip, nodeDetail } from "./lightsText";
import useLightsCanvas from "./useLightsCanvas";

const TOOLTIP_OFFSET_PX = 14;
const ICON_PX = 14;

/**
 * The canvas of the graph lights view plus its hover tooltip (node or edge) and the zoom controls. Focusable (role="img",
 * label rewritten per turn) so the keyboard controls of useLightsCanvas work. `controlsRef.current` receives
 * {recentre, zoomBy, flyTo}. `selection` is {node} | {edge} | null; `onSelect(selection)`.
 */
export default function LightsCanvas({ layout, lights, reducedMotion, label, controlsRef, options, themeMode, selection, hoverId, onSelect }) {
  const canvasRef = useRef(null);
  const { hover, recentre, zoomBy, flyTo, onKeyDown } = useLightsCanvas(canvasRef, {
    layout, lights, reducedMotion, options, themeMode, selection, hoverId, onSelect,
  });
  if (controlsRef) controlsRef.current = { recentre, zoomBy, flyTo };
  const clusterLabel = (nid) => {
    const id = layout.clusterOf.get(nid);
    return id ? layout.clusters.find((c) => c.id === id)?.label : null;
  };

  return (
    <div className="d5-lights-stage">
      <canvas ref={canvasRef} className={`d5-lights-canvas${hover ? " d5-lights-canvas--hit" : ""}`} role="img" aria-label={label} tabIndex={0} onKeyDown={onKeyDown} />
      {hover && (
        <div className="d5-lights-tip" style={{ left: hover.x + TOOLTIP_OFFSET_PX, top: hover.y + TOOLTIP_OFFSET_PX }} aria-hidden="true">
          {hover.node ? (
            <>
              <div className="d5-lights-tip-title">{hover.node.label}</div>
              <div className="d5-lights-tip-detail">{nodeDetail(hover.node, clusterLabel(hover.node.nid))}</div>
            </>
          ) : (
            <>
              <div className="d5-lights-tip-title">{edgeTooltip(hover.edge, lights).title}</div>
              <div className="d5-lights-tip-detail">{edgeTooltip(hover.edge, lights).detail}</div>
            </>
          )}
        </div>
      )}
      <div className="d5-lights-zoom" role="group" aria-label={LIGHTS_TEXT.controlsLabel}>
        <button type="button" className="d5-lights-icon-btn" onClick={() => zoomBy(CAMERA.KEY_ZOOM)} aria-label={LIGHTS_TEXT.zoomIn} title={LIGHTS_TEXT.zoomIn}>
          <Plus size={ICON_PX} aria-hidden="true" />
        </button>
        <button type="button" className="d5-lights-icon-btn" onClick={() => zoomBy(1 / CAMERA.KEY_ZOOM)} aria-label={LIGHTS_TEXT.zoomOut} title={LIGHTS_TEXT.zoomOut}>
          <Minus size={ICON_PX} aria-hidden="true" />
        </button>
        <button type="button" className="d5-lights-icon-btn" onClick={recentre} aria-label={LIGHTS_TEXT.recentreHint} title={LIGHTS_TEXT.recentreHint}>
          <LocateFixed size={ICON_PX} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
